/**
 * ============================================================================
 * EXECUTION METRICS DASHBOARD — KPI tile grid (per-network cards).
 * ============================================================================
 *
 * Tiles derive from the SAME getMetrics dataset as the tables/home page
 * (one cached call per (id, reportingPeriods, dataMode) — QuickSight's
 * single-dataset model). Tiles show one week: the running week in live
 * mode, the last COMPLETED week in snapshot (capture taken at week close).
 * Week arithmetic is backend-owned (getWeekNumbers) — never computed here.
 *
 * Filters: the shared filterQuery + shared FILTER_PROPERTIES and the
 * data-driven cascading options — one filter system across the app.
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@amzn/awsui-components-core/button';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { filterQueryChanged, dataModeChanged, demandCategoriesReplaced } from 'src/features/dashboard/dashboardSlice';
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';
import { NetworkId } from 'src/types';
import DataModeSelector from 'src/components/dashboard/DataModeSelector';
import Header from '@amzn/awsui-components-core/header';
import PropertyFilter from '@amzn/awsui-components-core/property-filter';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import NetworkCard from 'src/components/cards/NetworkCard';
import { NetworkCardData } from 'src/mocks/executionMetricsData';
import { useGetMetricsQuery } from 'src/features/api/metricsApi';
import { selectNetworkTiles } from 'src/features/api/tableSelectors';
import { buildDcrPredicate, selectedMetricGroups } from 'src/features/api/filterPredicate';
import { FILTER_PROPERTIES } from 'src/config/filterConfig';
import { useFilterOptions } from 'src/components/filters/useFilterOptions';
import { WBR_DASHBOARD_ID, WBR_REVIEW_TYPE } from 'src/constants';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { tilesWindow } from 'src/features/api/durationRange';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { useMetricsWindow } from 'src/features/api/useMetricsWindow';

import './ExecutionMetricsDashboard.scss';

const ExecutionMetricsDashboard: React.FC = () => {
  const { networkId } = useParams<{ networkId?: string }>();
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.dashboard.filterQuery);
  const setQuery = (q: typeof query) => dispatch(filterQueryChanged(q));

  const navigate = useNavigate();

  const dataMode = useAppSelector((s) => s.dashboard.dataMode);
  // DEFAULT WINDOW: no reportingPeriods — shares the app-wide default cache
  // entry (spec default-metrics-window R1). selectNetworkTiles reads the
  // CURRENT period (falls back to the latest returned — which for snapshot
  // IS the last completed capture).
  // Tiles read ONE week: the running week in live mode, the last completed
  // capture in snapshot. Requesting more would fetch data no tile renders.
  const { data: weekData } = useGetWeekNumbersQuery();
  const isSnapshot = dataMode === 'snapshot';
  const weeks = useMemo(
    () => (weekData ? tilesWindow(weekData.weeks, weekData.currentWeek, isSnapshot) : undefined),
    [weekData, isSnapshot],
  );
  // The other mode's week, warmed so the Live/Snapshot toggle does not reload.
  const otherModeWeeks = useMemo(
    () => (weekData ? tilesWindow(weekData.weeks, weekData.currentWeek, !isSnapshot) : undefined),
    [weekData, isSnapshot],
  );
  const { data: apiResponse } = useMetricsWindow({
    id: WBR_DASHBOARD_ID,
    dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE',
    weeks,
    universe: weekData?.weeks,
    alsoWarm: otherModeWeeks
      ? [{ dataMode: isSnapshot ? ('LIVE' as const) : ('SNAPSHOT' as const), weeks: otherModeWeeks }]
      : undefined,
  });

  // Metric catalog — one fetch per session, parallel with getMetrics.
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery({
    dashboardId: WBR_DASHBOARD_ID,
    reviewType: WBR_REVIEW_TYPE,
  });

  // Data-driven cascading dropdown values — same source as FilterBar.
  const filteringOptions = useFilterOptions();

  const networkTiles = useMemo((): NetworkCardData[] => {
    // Row-level filter chips constrain which DCRs feed each tile.
    const tiles = selectNetworkTiles(
      apiResponse,
      definitionsResponse?.definitions,
      buildDcrPredicate(query),
      selectedMetricGroups(query),
    );
    return tiles.map((tile) => ({
      id: tile.id,
      name: tile.name,
      description: '',
      type: '',
      size: '',
      onTimeDeliveryPct: tile.primaryRatePct,
      metrics: tile.metrics,
    }));
  }, [apiResponse, definitionsResponse, query]);

  // Selecting a network card opens the Detailed Summary page scoped to that
  // category — the checkbox row is the single source of scoping, so the click
  // writes the selection and lands on the canonical route.
  const handleCardSelect = useCallback((cardId: string) => {
    if ((DEMAND_CATEGORY_IDS as string[]).includes(cardId)) {
      dispatch(demandCategoriesReplaced([cardId as NetworkId]));
    }
    navigate('/metrics');
  }, [dispatch, navigate]);

  const selectedNetwork = networkId ? networkTiles.find(n => n.id === networkId) : null;

  const filteredNetworks = networkId
    ? networkTiles.filter(n => n.id === networkId)
    : networkTiles;

  return (
    <SpaceBetween size="l">
      <div className="dashboard-header-row">
        <Header
          variant="h1"
          actions={
            <Button variant="link" iconName="arrow-left" onClick={() => networkId ? navigate('/detail') : navigate('/')}>
              {networkId ? 'All Networks' : 'FAI - WBR Executive Summary'}
            </Button>
          }
        >
          {selectedNetwork ? selectedNetwork.name : 'FAI - WBR Customer Domain Level Summary'}
        </Header>
        <DataModeSelector
          mode={dataMode}
          onModeChange={(m) => dispatch(dataModeChanged(m))}
        />
      </div>

      <SpaceBetween size="s">
        <div className="filter-actions-row">
          <div className="filter-section">
            <PropertyFilter
              query={query}
              onChange={({ detail }) => setQuery(detail as typeof query)}
              filteringProperties={FILTER_PROPERTIES}
              filteringOptions={filteringOptions}
              disableFreeTextFiltering={true}
              i18nStrings={{
                filteringAriaLabel: 'Filter networks',
                filteringPlaceholder: 'Search or select a filter',
                groupValuesText: 'Values',
                groupPropertiesText: 'Properties',
                operatorsText: 'Operators',
                operationAndText: 'and',
                operationOrText: 'or',
                clearFiltersText: 'Clear filters',
                removeTokenButtonAriaLabel: (token) => `Remove filter ${token.propertyKey} ${token.operator} ${token.value}`,
                enteredTextLabel: (text) => `Use: "${text}"`,
              }}
            />
          </div>
        </div>

        <div className="network-cards-grid">
          {filteredNetworks.map(network => (
            <NetworkCard
              key={network.id}
              data={network}
              onSelect={() => handleCardSelect(network.id)}
            />
          ))}
        </div>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export { ExecutionMetricsDashboard };
export default ExecutionMetricsDashboard;
